import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { RootState } from "../../../../libraries/state_reducers/store";
import {
  toInputDate,
  toSaveDate,
  toSaveTime,
} from "../../../../service/date_service/date_service";
import UtilityHelper from "../../../../service/utilities/UtilityHelper";
import ShowToast from "../../../../widget/common/ui/Toast";
import AppTranslation from "../../../../service/app_translation/AppTranslation";
import SessionStorage from "../../../../service/storage/SessionStorage";
import StorageKeys from "../../../../const/StorageKeys";
import { ConversationList } from "../../../../model/chats/ChatListModel";
import ARFProvider from "./ARFProvider";
import { submitARFDetail } from "../../../../libraries/state_reducers/chats/ARFReducer";
import Loader from "../../../../widget/common/ui/Loader";
import { removeAllModal } from "../../../../libraries/state_reducers/widget/ModalsReducer";
import CacheStorage from "../../../../service/storage/CacheStorage";

type ARFContextType = {
  formType: "1" | "2" | "3";
  submitForm: boolean;
  fieldsValid: FormValidType;

  nameField: string;
  dateField: string;
  timeField: string;
  noPeopleField: string;
  subjectField: string;
  messageField: string;

  maxWords: number;

  handleOnChange: (
    name: string,
    value: string,
    isValid: boolean | undefined
  ) => void;

  handleSubmit: () => void;
  setupFormType: (fType: "1" | "2" | "3") => void;
};

type FormValidType = {
  name: boolean;
  date: boolean;
  phone: boolean;
  time: boolean;
  noPeople: boolean;
  subject: boolean;
  message: boolean;
};

const ARFContext = createContext<ARFContextType | undefined>(undefined);

export const ARFContextProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();

  const chatSelectedRef = useRef<ConversationList | null>(null);

  const maxWords: number = 2;
  const fieldsValidDefault: FormValidType = {
    name: false,
    date: false,
    phone: false,
    time: false,
    noPeople: false,
    subject: false,
    message: false,
  };

  const dateSelected: Date | null = useSelector(
    (state: RootState) => state.datePickerAlert.dateSelected
  );

  const formTypeSelector: "1" | "2" | "3" | null = useSelector(
    (state: RootState) => state.ARF.formType
  );

  const formTypeRef = useRef<string>("1");
  const fieldsValidRef = useRef<FormValidType>(fieldsValidDefault);

  const [fieldsValid, setFieldsValid] =
    useState<FormValidType>(fieldsValidDefault);

  const [formType, setFormType] = useState<"1" | "2" | "3">("1");

  const [nameField, setNameField] = useState<string>("");

  const [dateField, setDateField] = useState<string>("");
  const [timeField, setTimeField] = useState<string>("");

  const [noPeopleField, setNoPeopleField] = useState<string>("");

  const [subjectField, setSubjectField] = useState<string>("");
  const [messageField, setMessageField] = useState<string>("");

  const [submitForm, setSubmitForm] = useState<boolean>(false);

  const resetValues = () => {
    formTypeRef.current = "1";
    fieldsValidRef.current = fieldsValidDefault;
    setFieldsValid(fieldsValidDefault);

    setNameField("");
    setDateField("");
    setTimeField("");
    setNoPeopleField("");
    setSubjectField("");
    setMessageField("");

    setSubmitForm(false);
  };

  const setupFormType = (formType: "1" | "2" | "3") => {
    formTypeRef.current = formType;
    setFormType(formType);
    resetValues();
  };

  const handleOnChange = (
    fieldName: string,
    fieldValue: string,
    isValid: boolean | undefined
  ) => {
    fieldName = fieldName.toLowerCase();

    if (fieldName === "name") {
      setNameField(fieldValue);
      fieldsValidRef.current.name = isValid ?? false;
    } else if (fieldName === "date") {
      setDateField(fieldValue);
      fieldsValidRef.current.date = isValid ?? false;
    } else if (fieldName === "time") {
      setTimeField(fieldValue);
      fieldsValidRef.current.time = isValid ?? false;
    } else if (fieldName === "number_of_people") {
      setNoPeopleField(fieldValue);
      fieldsValidRef.current.noPeople = isValid ?? false;
    } else if (fieldName === "subject") {
      setSubjectField(fieldValue);
      fieldsValidRef.current.subject = isValid ?? false;
    } else if (fieldName === "message") {
      setMessageField(fieldValue);
      fieldsValidRef.current.message = isValid ?? false;
    }

    setFieldsValid(fieldsValidRef.current);
    setSubmitForm(false);
  };

  const handleSubmit = async () => {
    setSubmitForm(true);
    let validStatus: boolean = true;

    if (formType === "1") {
      if (
        [
          fieldsValidRef.current.date,
          fieldsValidRef.current.time,
          fieldsValidRef.current.subject,
          fieldsValidRef.current.message,
        ].includes(false)
      ) {
        validStatus = false;
      }
    } else if (formType === "2") {
      if (
        [
          fieldsValidRef.current.name,
          fieldsValidRef.current.date,
          fieldsValidRef.current.time,
          fieldsValidRef.current.message,
        ].includes(false)
      ) {
        validStatus = false;
      }
    } else if (formType === "3") {
      if (
        [
          fieldsValidRef.current.name,
          fieldsValidRef.current.date,
          fieldsValidRef.current.time,
          fieldsValidRef.current.noPeople,
          fieldsValidRef.current.message,
        ].includes(false)
      ) {
        validStatus = false;
      }
    }

    if (validStatus) {
      if (UtilityHelper.countWords(messageField) > maxWords) {
        ShowToast.warning(AppTranslation.text("character_exceeded"));
      } else {
        const conversStatus: boolean = getSelectedChatSession();
        if (conversStatus && "department_id" in chatSelectedRef.current!) {
          Loader.modal();

          const name: string = UtilityHelper.isNotEmpty(nameField)
            ? nameField
            : CacheStorage.getValue(StorageKeys.name);

          const statusResponse: boolean = await ARFProvider.submitARFAPI({
            formStatus: formType,
            conversationId: chatSelectedRef.current?.conv_id!,
            departmentId: chatSelectedRef.current?.department_id!,
            name: name,
            date: toSaveDate(dateField),
            time: toSaveTime(timeField),
            noPeople: noPeopleField,
            subject: subjectField,
            message: messageField,
          });
          Loader.modal(false);

          if (statusResponse) {
            dispatch(
              submitARFDetail({
                name: nameField,
                date: dateField,
                time: timeField,
                noPeople: noPeopleField,
                subject: subjectField,
                message: messageField,
              })
            );

            closeARFForm();
          }
        }
      }
    }
  };

  const closeARFForm = () => {
    dispatch(removeAllModal());
    window.history.replaceState(null, "", window.location.pathname);
  };

  const getSelectedChatSession = (): boolean => {
    const chatsChatSelectedSession = SessionStorage.getValue(
      StorageKeys.chats_chat_selected
    );

    if (UtilityHelper.isNotEmpty(chatsChatSelectedSession)) {
      const chatSelectedSession: ConversationList = JSON.parse(
        chatsChatSelectedSession
      );

      chatSelectedRef.current = chatSelectedSession;
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (dateSelected != null) {
      setDateField(toInputDate(dateSelected!));
      fieldsValidRef.current.date = true;
      setSubmitForm(false);
    }
  }, [dateSelected]);

  useEffect(() => {
    setupFormType(formTypeSelector);

    return () => {
      resetValues();
    };
    // eslint-disable-next-line
  }, [formTypeSelector]);

  return (
    <ARFContext.Provider
      value={{
        formType,
        submitForm,
        fieldsValid,

        nameField,
        dateField,
        timeField,
        noPeopleField,
        subjectField,
        messageField,

        maxWords,

        setupFormType,
        handleOnChange,
        handleSubmit,
      }}
    >
      {children}
    </ARFContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const ARFContextImport = () => {
  const context = useContext(ARFContext);
  if (!context) {
    throw new Error(
      "ARFContextImport must be used within a ARFContextProvider"
    );
  }
  return context;
};
